import { ref, watch } from 'vue'
import { Capacitor } from '@capacitor/core'

import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { useUserStore } from '@/store/user'
import { prepareResponseError } from '@/store/utils/helpers'

import { fetchDeployedVersion } from './utils/helpers'

// Kept in sync with `VERSION_FILE_NAME` in utils/const.ts, which is what the
// build writes into `public/`.
const VERSION_FILE_URL = `${import.meta.env.BASE_URL}version.json`

export const useVersionService = () => {
  const { info } = useNotifications()

  const userStore = useUserStore()

  const isChecking = ref(false)
  const isNotified = ref(false)

  const check = async () => {
    // The native shells run an embedded bundle — reloading cannot pull a newer
    // build, so there is nothing to prompt for. Once the user has been asked,
    // don't nag again for the lifetime of this page.
    if (Capacitor.isNativePlatform() || isChecking.value || isNotified.value)
      return
    isChecking.value = true
    try {
      const deployedVersion = await fetchDeployedVersion(VERSION_FILE_URL)
      if (deployedVersion === __APP_VERSION__) return
      isNotified.value = true
      const isMac = navigator.userAgent.includes('Mac')
      const refreshLabel = `Refresh ${isMac ? '⌘' : 'Ctrl'} + R`
      await info({
        message: 'Allposit is ready to update!',
        description: 'Refresh to see what is new.',
        size: 'sm',
        actions: [
          {
            label: refreshLabel,
            attrs: {
              'data-refid': 'newVersionRefreshButton',
              class: 'w-full',
            },
            onClick: () => {
              window.location.reload()
            },
          },
        ],
      })
    } catch (e) {
      // A failed check must not break the app, but it must be visible: without
      // this log a missing or broken version file would silently disable
      // update prompts for everyone.
      handleCatchedError(`App version check failed: ${prepareResponseError(e)}`)
    } finally {
      isChecking.value = false
    }
  }

  // Every app refresh, plus every login — a session that starts on the sign-in
  // page can be older than the build the user lands on after authenticating.
  watch(
    () => userStore.isAuthenticated,
    isAuthenticated => {
      isAuthenticated && check()
    },
    { immediate: true },
  )
}
